import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, NgModule, OnInit } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { ActivatedRoute } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { PipesModule } from '@shared/pipes/pipes.module';
import { Chart } from 'chart.js';
import { OwlDateTimeModule } from 'ng-pick-datetime-ex';
import { ChartsModule } from 'ng2-charts';

export interface dateRangeE {
  name: string;
  value: number;
}
export enum DateRange {
  Weekly = 0,
  Monthly = 1,
  Quarterly = 2,
  Yearly = 3,
}
export interface GetOrderStatesReportRes {
  id: string;
  clientName: string;
  totalOrders: number;
  totalOrdersAmount: number;
  averageOrderAmount: number;
  weeklyReports: WeeklyReport[];
}

export interface WeeklyReport {
  labels: string;
  totalOrders: number;
  totalOrdersAmount: number;
  averageOrderAmount: number;
}
@Component({
  selector: 'app-mobile-web-view-client-report',
  templateUrl: './mobile-web-view-client-report.component.html',
  styleUrls: ['./mobile-web-view-client-report.component.scss'],
})
export class MobileWebViewClientReportComponent implements OnInit {
  minDate: Date = new Date(
    new Date().getFullYear(),
    new Date().getMonth(),
    new Date().getDate()
  );
  public orderStatsData: GetOrderStatesReportRes = <GetOrderStatesReportRes>{};
  chart: any = [];
  selectionList: dateRangeE[] = [
    { name: 'Weekly', value: 0 },
    { name: 'Monthly', value: 1 },
    { name: 'Quarterly', value: 2 },
    { name: 'Yearly', value: 3 },
  ];
  customerId: string = '';
  dateRangeControl: FormControl = new FormControl('');
  isLoading: boolean = false;
  constructor(private http: HttpClient, private route: ActivatedRoute) {
    this.dateRangeControl.setValue(1);
  }

  ngOnInit(): void {
    this.route.params.subscribe((cId) => {
      if (cId && Object.keys(cId).length > 0 && cId['id']) {
        this.customerId = cId['id'];
        const getDto = {
          customerId: this.customerId,
          dateRange: this.dateRangeControl.value,
        };
        this.getAllOrderStatesReport(getDto);
      }
    });
  }
  // Define a plugin to display a "No data available" message
  noDataPlugin = {
    id: 'noDataPlugin',
    beforeDraw: (chart) => {
      const ctx = chart.ctx;
      const data = chart.data;

      if (data.datasets.length === 0 || data.datasets[0].data.length === 0) {
        ctx.save();
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.font = '16px Arial';
        ctx.fillStyle = '#0069ff';
        const { width, height } = chart;
        ctx.fillText('No data available', width / 2, height / 2);
        ctx.restore();
      }
    },
  };

  private getAllOrderStatesReport(data) {
    this.isLoading = true;
    this.getOrderStatsReport(data).subscribe((res: GetOrderStatesReportRes) => {
      this.isLoading = false;
      if (res) {
        this.orderStatsData = res;
        this.chart = new Chart('canvas', {
          type: 'bar',
          plugins: [this.noDataPlugin],
          options: {
            responsive: true,
            legend: {
              labels: {
                usePointStyle: true,
              },
            },
            scales: {
              yAxes: [
                {
                  ticks: {
                    beginAtZero: true,
                    callback(value, index, values) {
                      return value === 0
                        ? value
                        : Number(value.toLocaleString().split(',')[0]) + 'k';
                    },
                    stepSize: 1000,
                  },
                },
              ],
            },
          },
          data: {
            labels: this.orderStatsData.weeklyReports.map((s) => s.labels),
            datasets: [
              {
                data: this.orderStatsData.weeklyReports.map(
                  (s) => s.totalOrders
                ),
                label: 'Total Orders',
                backgroundColor: '#0069FF',
                barThickness: 15,
              },
              {
                data: this.orderStatsData.weeklyReports.map(
                  (s) => s.averageOrderAmount
                ),
                label: 'Avg. Orders Amount',
                backgroundColor: '#D7DFF2',
                barThickness: 15,
              },
              {
                data: this.orderStatsData.weeklyReports.map(
                  (s) => s.totalOrdersAmount
                ),
                label: 'Total Orders Amount',
                backgroundColor: '#031B4E',
                barThickness: 15,
              },
            ],
          },
        });
      } else {
        this.orderStatsData = <GetOrderStatesReportRes>{};
        this.chart = new Chart('canvas', {
          type: 'bar',
          plugins: [this.noDataPlugin],
          options: {},
          data: {},
        });
      }
    });
  }

  compareFn(obj1: any, obj2: any) {
    return obj1 && obj2 ? obj1.id === obj2.id : obj1 === obj2;
  }

  private getOrderStatsReport(data) {
    const url = 'MobileView/OrderStatesReport';
    return this.http.get<GetOrderStatesReportRes>(url, { params: data });
  }

  getData(event: MatSelectChange) {
    if (event) {
      const getDto = {
        customerId: this.customerId,
        dateRange: event.value,
      };
      this.getAllOrderStatesReport(getDto);
    }
  }
}

@NgModule({
  declarations: [MobileWebViewClientReportComponent],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatChipsModule,
    MatProgressSpinnerModule,
    TranslateModule,
    OwlDateTimeModule,
    ChartsModule,
    MatMenuModule,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    PipesModule,
  ],
})
export class MobileWebViewClientReportModule {}
